/**
 * EPG Zustand Store
 *
 * Global state management for Event Planning Grid.
 * Handles organizations, events, filters, and unsaved changes.
 *
 * Created: 2025-12-14
 * Updated: 2025-12-15 - Migrated to MonthCode enum (JAN, FEB, MAR)
 * Phase: 2 - Calendar Grid UI
 *
 * Features:
 * - Organization list with pagination and search
 * - Event calendar data for selected organization
 * - Available events catalog
 * - Unsaved changes tracking with localStorage persistence
 * - Optimistic updates for better UX
 * - Filters and view preferences
 * - MonthCode enum usage (matches backend)
 * - Max 15 events per month validation
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useEffect } from 'react';
import { logger } from '@shared/utils/logger';
import { epgApi } from '@shared/services/epgApi';
import { useToastStore } from '@shared/store/toastStore';
import { getCategoryOptions, normalizeCategoryName } from '@features/epg/config/epgConfig';
import { createCloudSync } from '@shared/store/zustandCloudSync';
import {
  EVENT_FIELD_HASHES,
  CALLS_EMAILS_FIELD_HASHES,
  DROPS_FIELD_HASHES,
} from '@features/epg/utils/epgFieldMapping';
import {
  DEFAULT_EPG_PREFERENCES,
  MonthCode,
  validateEventIds,
} from '@shared/types/epg';
import type {
  EPGOrganization,
  EventsByMonth,
  ContactInfo,
  ContactGrouping,
  OrganizationDetail,
  EPGFilters,
  EPGViewPreferences,
  EPGUnsavedChanges,
  EventOptionsResponse,
  BatchUpdateItem,
  SavedFilterPreset,
  FieldDefinition,
  PendingChange,
  EventOptionsByCategory,
  CategoryEventOptions,
} from '@shared/types/epg';

/**
 * EPG View Mode
 */
export type EPGViewMode = 'spreadsheet' | 'single-company';

/**
 * Contact Event Type Edit
 * Tracks pending changes to contact event preferences
 */
export interface ContactEventEdit {
  contactId: number;
  contactName: string;
  orgId: number;
  orgName: string;
  originalEventTypes: string[];  // Original values from Pipedrive (e.g., ["Education", "Sporting"])
  newEventTypes: string[];       // Modified values (e.g., ["Education", "Webinar"])
  timestamp: number;
}

/**
 * Contact Event Types Configuration
 * Maps to Pipedrive field key: 39ec70663687fbd740bdaa0191c05f03c2472dda
 */
export const CONTACT_EVENT_TYPES = [
  { id: 1914, key: 'education', label: 'Edu', fullLabel: 'Education', color: '#3b82f6' },
  { id: 1915, key: 'sporting', label: 'Sport', fullLabel: 'Sporting', color: '#10b981' },
  { id: 1916, key: 'concerts', label: 'Music', fullLabel: 'Concerts', color: '#8b5cf6' },
  { id: 1917, key: 'tastings', label: 'Taste', fullLabel: 'Tastings & HH', color: '#f59e0b' },
  { id: 1918, key: 'outdoors', label: 'Out', fullLabel: 'Outdoors', color: '#06b6d4' },
  { id: 1919, key: 'usi_eb', label: 'USI', fullLabel: 'USI EB Events', color: '#ec4899' },
  { id: 1962, key: 'webinar', label: 'Web', fullLabel: 'Webinar', color: '#6366f1' },
] as const;

/**
 * Pipedrive field key for Contact Event Types
 */
export const CONTACT_EVENT_TYPES_FIELD_KEY = '39ec70663687fbd740bdaa0191c05f03c2472dda';

/**
 * EPG Store State
 */
interface EPGState {
  // ======================================
  // Organizations
  // ======================================
  organizations: EPGOrganization[];
  totalOrganizations: number;
  currentPage: number;
  perPage: number;
  totalPages: number;

  // ======================================
  // Selected Organization & Events
  // ======================================
  selectedOrgId: number | null;
  selectedOrgName: string | null;
  selectedOrganization: OrganizationDetail | null; // Full organization detail with contacts
  eventsByMonth: Record<string, EventsByMonth> | null; // MonthCode -> EventsByMonth
  lastSelectedOrgId: number | null; // Last selected company for persistence

  // ======================================
  // Available Events
  // ======================================
  eventOptions: EventOptionsResponse | null; // Event options by category (DEPRECATED - use eventOptionsByCategory)
  eventOptionsByCategory: EventOptionsByCategory; // Event options organized by category with months (Phase 1)
  organizationContacts: ContactInfo[]; // Contacts for selected organization

  // ======================================
  // Field Definitions (Pipedrive Parity)
  // ======================================
  fieldDefinitions: Record<string, FieldDefinition> | null; // field_key -> definition
  fieldDefinitionsCategories: Record<string, string[]> | null; // category -> field_keys[]
  fieldDefinitionsLastSync: string | null; // ISO timestamp
  isLoadingFieldDefinitions: boolean;
  isSyncingFieldDefinitions: boolean;

  // ======================================
  // Filters & Preferences
  // ======================================
  filters: EPGFilters;
  viewPreferences: EPGViewPreferences;
  selectedMonth: MonthCode; // Currently selected month for editing
  selectedOrgYear: number; // Currently selected year for calendar view
  visibleCategories: Set<string>; // Categories to show (Events, Drops, Calls/Emails)
  sortField: string | null; // P4-T6: Field to sort by (e.g., 'name', 'address', 'owner')
  sortDirection: 'asc' | 'desc'; // P4-T6: Sort direction

  // ======================================
  // UI State
  // ======================================
  isLoadingOrganizations: boolean;
  isLoadingEvents: boolean;
  isLoadingEventOptions: boolean;
  isSyncing: boolean; // Syncing to Pipedrive
  isSaving: boolean; // Alias for isSyncing (used by toolbar)
  error: string | null;
  isPendingChangesModalOpen: boolean; // Track if pending changes modal is open
  lastOrganizationsFetchTime: number | null; // Timestamp of last organization fetch (for client-side caching)

  // ======================================
  // Palette Drag & Drop State (Single-Month Mode)
  // ======================================
  isDraggingFromPalette: boolean; // Whether user is dragging from Event Palette
  draggedCategory: string | null; // Category of dragged event (Events, Drops, Calls/Emails)
  draggedEventId: number | null; // ID of the event being dragged
  draggedEventLabel: string | null; // Label of the event being dragged
  draggedEventColor: string | null; // Color for glow effect

  // Multi-Add Mode (Shift+Drop to enter, release Shift to exit)
  // ======================================
  isMultiAddMode: boolean; // Whether user is in multi-add stamp mode
  multiAddCategory: string | null; // Category for multi-add
  multiAddEventId: number | null; // Event ID for multi-add
  multiAddEventLabel: string | null; // Label for multi-add overlay
  multiAddEventColor: string | null; // Color for multi-add overlay

  // ======================================
  // Keyboard Navigation (P2-T09)
  // ======================================
  focusedCell: string | null; // Current focused cell (orgId-category-month)
  setFocusedCell: (cellKey: string | null) => void;

  // ======================================
  // Unsaved Changes (Pending)
  // ======================================
  unsavedChanges: EPGUnsavedChanges | null;

  // ======================================
  // Cell-Level Change Tracking (P2-T07)
  // ======================================
  // Map of cell keys (orgId-category-month) to original and new values
  cellEdits: Map<string, { orgId: number; category: string; month: MonthCode; oldIds: number[]; newIds: number[] }>;

  // ======================================
  // Contact Event Type Edits (Phase: Contact Events)
  // ======================================
  // Map of contactId to pending event type changes
  contactEdits: Map<number, ContactEventEdit>;

  // ======================================
  // Multi-Select (Checkboxes)
  // ======================================
  selectedOrgIds: Set<number>;

  // ======================================
  // Contact Selection & Grouping
  // ======================================
  selectedContactIds: Set<string>; // person_id as string for Set compatibility
  contactGroupings: ContactGrouping[]; // Company bands with alternating colors

  // ======================================
  // Saved Filter Presets (Phase 4)
  // ======================================
  savedFilters: Record<string, SavedFilterPreset>; // name -> preset

  // ======================================
  // Actions: Organizations
  // ======================================
  loadOrganizations: (page?: number, limit?: number) => Promise<void>;
  searchOrganizations: (query: string) => Promise<void>;
  selectOrganization: (orgId: number) => Promise<void>;
  clearSelection: () => void;

  // ======================================
  // Actions: Multi-Select
  // ======================================
  toggleOrgSelection: (orgId: number) => void;
  selectAllOrgs: () => void;
  deselectAllOrgs: () => void;
  selectRange: (startOrgId: number, endOrgId: number) => void; // P2-T5: Range selection
  getSelectedOrgIds: () => number[];

  // ======================================
  // Actions: Contact Selection
  // ======================================
  selectContact: (personId: string) => void;
  deselectContact: (personId: string) => void;
  selectAllContacts: () => void;
  deselectAllContacts: () => void;
  setContactGroupings: (groupings: ContactGrouping[]) => void;

  // ======================================
  // Actions: Events
  // ======================================
  getStaticEventOptions: (category: 'Events' | 'Calls & Emails' | 'Drops') => CategoryEventOptions;
  transformEventOptions: (response: EventOptionsResponse | null, category: 'Events' | 'Calls & Emails' | 'Drops') => CategoryEventOptions;
  loadEventOptions: (category: 'Events' | 'Calls & Emails' | 'Drops') => Promise<void>;
  toggleEvent: (month: MonthCode, eventId: number) => void;
  updateMonthEvents: (month: MonthCode, eventIds: number[]) => void;
  updateCellEvents: (orgId: number, category: string, month: MonthCode, eventIds: number[], oldIds?: number[]) => void; // Cell-level tracking
  clearCategoryEvents: (orgId: number, category: string, visibleMonthKeys?: MonthCode[]) => void; // Clear events for a category row (visible months only - P1-T2)
  commitSync: () => Promise<void>;
  saveChanges: () => Promise<void>; // Alias for commitSync
  discardChanges: () => void;
  discardCellEdits: (cellKeys: string[]) => void; // Discard specific cells (Phase 4)
  refreshFromPipedrive: () => Promise<void>; // Pull from Pipedrive (P5-T02)
  pushToPipedrive: () => Promise<void>; // Push to Pipedrive (P5-T01)

  // ======================================
  // Actions: Field Definitions (Pipedrive Parity)
  // ======================================
  loadFieldDefinitions: (category?: string) => Promise<void>;
  syncFieldDefinitions: () => Promise<void>;

  // ======================================
  // Actions: Pending Changes (Phase 6)
  // ======================================
  loadPendingChanges: () => Promise<void>;
  savePendingChangeToBackend: (orgId: number, orgName: string, category: string, month: MonthCode, oldIds: number[], newIds: number[]) => Promise<void>;
  discardPendingChange: (orgId: number, category: string, month: MonthCode) => Promise<void>;
  discardAllPendingChanges: () => Promise<void>;
  clearDeletedOrgChanges: () => Promise<void>;

  // ======================================
  // Actions: Contact Event Type Edits
  // ======================================
  toggleContactEventType: (contact: { id: number; name: string; org_id: number | null; organization_name?: string }, eventType: string) => void;
  discardContactEdit: (contactId: number) => void;
  discardAllContactEdits: () => void;
  pushContactChanges: () => Promise<void>;
  getContactPendingEventTypes: (contactId: number) => string[] | null;

  // ======================================
  // Actions: Filters & Preferences
  // ======================================
  updateFilters: (filters: Partial<EPGFilters>) => void;
  updateViewPreferences: (preferences: Partial<EPGViewPreferences>) => void;
  resetFilters: () => void;
  setSelectedMonth: (month: MonthCode) => void;
  setSelectedYear: (year: number) => void;
  setVisibleCategories: (categories: Set<string>) => void;
  toggleCategoryVisibility: (category: string) => void;
  setSortField: (field: string | null, direction?: 'asc' | 'desc') => void; // P4-T6

  // ======================================
  // Actions: Palette Drag & Drop
  // ======================================
  startPaletteDrag: (category: string, eventId: number, eventLabel: string, color: string) => void;
  endPaletteDrag: () => void;

  // Actions: Multi-Add Mode (Shift+Click Stamp Mode)
  // ======================================
  startMultiAddMode: (category: string, eventId: number, eventLabel: string, color: string) => void;
  endMultiAddMode: () => void;

  // ======================================
  // Actions: Saved Filter Presets (Phase 4)
  // ======================================
  saveFilterPreset: (name: string) => void;
  loadFilterPreset: (name: string) => void;
  deleteFilterPreset: (name: string) => void;
  getSavedFilterNames: () => string[];

  // ======================================
  // Actions: Error Handling
  // ======================================
  clearError: () => void;

  // ======================================
  // Actions: UI State
  // ======================================
  setPendingChangesModalOpen: (isOpen: boolean) => void;

  // ======================================
  // Internal State (Undo/Redo)
  // ======================================
  undoStack: Array<Record<string, EventsByMonth>>;
  redoStack: Array<Record<string, EventsByMonth>>;
  undo: () => void;
  redo: () => void;

  // ======================================
  // Single Company View (Phase: Single Company View)
  // ======================================
  viewMode: EPGViewMode;
  currentCompanyIndex: number;

  // Actions: Single Company Navigation
  setViewMode: (mode: EPGViewMode) => void;
  setCurrentCompanyIndex: (index: number) => void;
  navigateToNextCompany: () => void;
  navigateToPreviousCompany: () => void;
  navigateToCompany: (orgId: number) => void;

  // Selectors: Single Company View
  getSortedOrganizations: () => EPGOrganization[];
  getCurrentOrganization: () => EPGOrganization | null;

  // ======================================
  // Cell Edit Panel (Sidebar Edit Mode)
  // ======================================
  editingCell: {
    orgId: number;
    category: string;
    month: MonthCode;
    orgName: string;
  } | null;

  // Actions: Cell Edit Panel
  startCellEdit: (orgId: number, category: string, month: MonthCode, orgName: string) => void;
  endCellEdit: () => void;
}

/**
 * Default filters
 */
const defaultFilters: EPGFilters = {
  search: '',
  sortBy: 'name',
  sortOrder: 'asc',
  quickFilters: {
    hasEvents: false,
    recentActivity: false,
  },
  category: 'All',
  month: 'Any Month',
  eventOption: null,
  inverseFilter: false,
  advancedFilters: [],
  savedFilterName: null,
};

/**
 * EPG Zustand Store
 */
export const useEPGStore = create<EPGState>()(
  devtools(
    persist(
      (set, get) => ({
        // ======================================
        // Initial State
        // ======================================
        organizations: [],
        totalOrganizations: 0,
        currentPage: 1,
        perPage: 50,
        totalPages: 0,

        selectedOrgId: null,
        selectedOrgName: null,
        selectedOrganization: null,
        eventsByMonth: null,
        lastSelectedOrgId: null,

        eventOptions: null,
        eventOptionsByCategory: {
          'Events': {
            months: {
              JAN: [], FEB: [], MAR: [], APR: [], MAY: [], JUN: [],
              JUL: [], AUG: [], SEP: [], OCT: [], NOV: [], DEC: []
            },
            isLoading: false,
            lastLoaded: null,
          },
          'Calls & Emails': {
            months: {
              JAN: [], FEB: [], MAR: [], APR: [], MAY: [], JUN: [],
              JUL: [], AUG: [], SEP: [], OCT: [], NOV: [], DEC: []
            },
            isLoading: false,
            lastLoaded: null,
          },
          'Drops': {
            months: {
              JAN: [], FEB: [], MAR: [], APR: [], MAY: [], JUN: [],
              JUL: [], AUG: [], SEP: [], OCT: [], NOV: [], DEC: []
            },
            isLoading: false,
            lastLoaded: null,
          },
        },
        organizationContacts: [],

        fieldDefinitions: null,
        fieldDefinitionsCategories: null,
        fieldDefinitionsLastSync: null,
        isLoadingFieldDefinitions: false,
        isSyncingFieldDefinitions: false,

        filters: defaultFilters,
        viewPreferences: DEFAULT_EPG_PREFERENCES,
        selectedMonth: MonthCode.JAN, // Default to January
        selectedOrgYear: new Date().getFullYear(), // Default to current year
        visibleCategories: new Set(['Events', 'Drops', 'Calls/Emails']), // All visible by default
        sortField: 'name', // P4-T6: Default sort by name
        sortDirection: 'asc' as 'asc' | 'desc', // P4-T6: Default ascending

        isLoadingOrganizations: false,
        isLoadingEvents: false,
        isLoadingEventOptions: false,
        isSyncing: false,
        isSaving: false,
        error: null,
        lastOrganizationsFetchTime: null, // Initialize cache timestamp
        isPendingChangesModalOpen: false, // Initialize modal state

        // Palette Drag & Drop (Single-Month Mode)
        isDraggingFromPalette: false,
        draggedCategory: null,
        draggedEventId: null,
        draggedEventLabel: null,
        draggedEventColor: null,

        // Multi-Add Mode (Shift+Click Stamp Mode)
        isMultiAddMode: false,
        multiAddCategory: null,
        multiAddEventId: null,
        multiAddEventLabel: null,
        multiAddEventColor: null,

        focusedCell: null,

        unsavedChanges: null,

        cellEdits: new Map(),

        contactEdits: new Map<number, ContactEventEdit>(),

        selectedOrgIds: new Set<number>(),

        selectedContactIds: new Set<string>(),
        contactGroupings: [],

        savedFilters: {},

        undoStack: [],
        redoStack: [],

        // Single Company View State
        viewMode: 'spreadsheet' as EPGViewMode,
        currentCompanyIndex: 0,

        // Cell Edit Panel State (Sidebar Edit Mode)
        editingCell: null,

        // ======================================
        // Organizations Actions
        // ======================================

        /**
         * Load organizations list with pagination
         *
         * RATE LIMITING PROTECTION:
         * - Client-side caching with 5-minute TTL
         * - Debouncing to prevent concurrent calls
         * - Minimum 2-second interval between calls
         */
        loadOrganizations: async (page = 1, limit = 50) => {
          const state = get();
          const { filters, isLoadingOrganizations, lastOrganizationsFetchTime } = state;

          // DEBOUNCE: Prevent concurrent calls
          if (isLoadingOrganizations) {
            logger.warn('[EPG Store] loadOrganizations already in progress, skipping duplicate call');
            return;
          }

          // CLIENT-SIDE CACHE: Check if we have fresh data (5-minute TTL)
          const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
          const now = Date.now();
          const timeSinceLastFetch = lastOrganizationsFetchTime ? now - lastOrganizationsFetchTime : Infinity;

          if (state.organizations.length > 0 && timeSinceLastFetch < CACHE_TTL_MS) {
            logger.log(
              `[EPG Store] Using cached organizations (${Math.round(timeSinceLastFetch / 1000)}s old, TTL: ${CACHE_TTL_MS / 1000}s)`
            );
            return;
          }

          // MINIMUM INTERVAL: Prevent rapid-fire calls (2-second minimum)
          const MIN_INTERVAL_MS = 2000; // 2 seconds
          if (timeSinceLastFetch < MIN_INTERVAL_MS) {
            logger.warn(
              `[EPG Store] Rate limit protection: Call too soon (${Math.round(timeSinceLastFetch / 1000)}s ago, min: ${MIN_INTERVAL_MS / 1000}s)`
            );
            // BUGFIX (P1-T1): Update timestamp on early return to prevent infinite re-render loop
            // Without this, the component keeps calling loadOrganizations because the check at line 384-389 will always pass
            set({ lastOrganizationsFetchTime: now });
            return;
          }

          set({ isLoadingOrganizations: true, error: null });

          try {
            logger.log(`[EPG Store] Loading organizations (page=${page}, limit=${limit})`);

            const result = await epgApi.getOrganizations({
              page,
              limit,
              search: filters.search || null,
            });

            set({
              organizations: result.organizations,
              totalOrganizations: result.total,
              currentPage: result.page,
              perPage: result.per_page,
              totalPages: result.total_pages,
              isLoadingOrganizations: false,
              lastOrganizationsFetchTime: Date.now(), // Update cache timestamp
            });

            logger.log(`[EPG Store] Loaded ${result.organizations.length} organizations`);
          } catch (error) {
            logger.error('[EPG Store] Failed to load organizations:', error);

            // Check for 429 rate limit error
            const is429Error = error instanceof Error && error.message.includes('429');
            const errorMessage = is429Error
              ? 'Rate limit exceeded. Please wait a moment and try again.'
              : error instanceof Error ? error.message : 'Failed to load organizations';

            // BUGFIX (P1-T1): Update timestamp on error to prevent infinite retry loop
            set({
              error: errorMessage,
              isLoadingOrganizations: false,
              lastOrganizationsFetchTime: Date.now(),
            });
          }
        },

        /**
         * Search organizations by name
         */
        searchOrganizations: async (query: string) => {
          set((state) => ({
            filters: {
              ...state.filters,
              search: query,
            },
          }));

          // Reset to page 1 and reload
          await get().loadOrganizations(1);
        },

        /**
         * Select organization and load its detail, events, and contacts
         */
        selectOrganization: async (orgId: number) => {
          const { organizations } = get();

          // Find organization name
          const org = organizations.find((o) => o.id === orgId);
          const orgName = org?.name || `Organization ${orgId}`;

          set({
            selectedOrgId: orgId,
            selectedOrgName: orgName,
            lastSelectedOrgId: orgId,  // Save last selected company
            isLoadingEvents: true,
            error: null,
            unsavedChanges: null, // Clear unsaved changes when switching orgs
            cellEdits: new Map(), // Clear cell-level edits when switching orgs
            undoStack: [],
            redoStack: [],
          });

          try {
            logger.log(`[EPG Store] Loading data for org ${orgId} (${orgName})`);

            // Fetch organization detail, events, and contacts in parallel
            const [orgDetail, eventsData, contacts] = await Promise.all([
              epgApi.getOrganization(orgId),
              epgApi.getOrganizationEvents(orgId),
              epgApi.getOrganizationContacts(orgId),
            ]);

            // FIX: Merge API data with category-specific arrays extracted from organization
            // This ensures we have proper category separation even if API doesn't return it
            let mergedEventsByMonth = eventsData.events_by_month;
            if (org) {
              // Import extractEventsFromOrganization dynamically to avoid circular deps
              const { extractEventsFromOrganization } = await import('@features/epg/utils/epgFieldMapping');
              const extractedData = extractEventsFromOrganization(org as EPGOrganization & Record<string, unknown>);

              // Merge: Use API data as base, but ensure category-specific arrays from extracted
              mergedEventsByMonth = { ...eventsData.events_by_month };
              for (const [monthCode, extractedMonth] of Object.entries(extractedData)) {
                const apiMonth = mergedEventsByMonth[monthCode] || {};
                mergedEventsByMonth[monthCode] = {
                  ...apiMonth,
                  // Preserve category-specific arrays from extraction
                  events_selected_ids: extractedMonth.events_selected_ids || apiMonth.events_selected_ids || [],
                  calls_emails_selected_ids: extractedMonth.calls_emails_selected_ids || apiMonth.calls_emails_selected_ids || [],
                  drops_selected_ids: extractedMonth.drops_selected_ids || apiMonth.drops_selected_ids || [],
                  // Combined selected_ids for backward compatibility
                  selected_ids: apiMonth.selected_ids || extractedMonth.selected_ids || [],
                };
              }
              logger.log(`[EPG Store] Merged API data with category-specific arrays for org ${orgId}`);
            }

            set({
              selectedOrganization: orgDetail,
              eventsByMonth: mergedEventsByMonth,
              organizationContacts: contacts,
              isLoadingEvents: false,
            });

            logger.log(
              `[EPG Store] Loaded org ${orgId}: ${contacts.length} contacts, ${Object.keys(mergedEventsByMonth).length} months`
            );
          } catch (error) {
            logger.error(`[EPG Store] Failed to load data for org ${orgId}:`, error);
            set({
              error: error instanceof Error ? error.message : 'Failed to load organization data',
              isLoadingEvents: false,
            });
          }
        },

        /**
         * Clear organization selection
         */
        clearSelection: () => {
          set({
            selectedOrgId: null,
            selectedOrgName: null,
            selectedOrganization: null,
            eventsByMonth: null,
            organizationContacts: [],
            unsavedChanges: null,
            cellEdits: new Map(),
            undoStack: [],
            redoStack: [],
          });
        },

        // ======================================
        // Multi-Select Actions
        // ======================================

        /**
         * Toggle organization selection (checkbox)
         */
        toggleOrgSelection: (orgId: number) => {
          set((state) => {
            const newSelectedOrgIds = new Set(state.selectedOrgIds);
            if (newSelectedOrgIds.has(orgId)) {
              newSelectedOrgIds.delete(orgId);
            } else {
              newSelectedOrgIds.add(orgId);
            }
            logger.log(`[EPG Store] Toggled org ${orgId} selection. Total selected: ${newSelectedOrgIds.size}`);
            return { selectedOrgIds: newSelectedOrgIds };
          });
        },

        /**
         * Select all organizations on current page
         */
        selectAllOrgs: () => {
          set((state) => {
            const allOrgIds = new Set(state.organizations.map((org) => org.id));
            logger.log(`[EPG Store] Selected all ${allOrgIds.size} organizations`);
            return { selectedOrgIds: allOrgIds };
          });
        },

        /**
         * Deselect all organizations
         */
        deselectAllOrgs: () => {
          logger.log('[EPG Store] Deselected all organizations');
          set({ selectedOrgIds: new Set<number>() });
        },

        /**
         * Select a range of organizations (Shift+Click support)
         * P2-T5: Keyboard shortcuts for selection
         */
        selectRange: (startOrgId: number, endOrgId: number) => {
          set((state) => {
            const orgs = state.organizations;
            const startIdx = orgs.findIndex((o) => o.id === startOrgId);
            const endIdx = orgs.findIndex((o) => o.id === endOrgId);

            if (startIdx === -1 || endIdx === -1) {
              logger.warn(`[EPG Store] Range selection failed: invalid org IDs ${startOrgId} to ${endOrgId}`);
              return state;
            }

            // Determine range direction
            const minIdx = Math.min(startIdx, endIdx);
            const maxIdx = Math.max(startIdx, endIdx);

            // Select all organizations in range
            const newSelectedOrgIds = new Set(state.selectedOrgIds);
            for (let i = minIdx; i <= maxIdx; i++) {
              newSelectedOrgIds.add(orgs[i].id);
            }

            logger.log(`[EPG Store] Range selected ${maxIdx - minIdx + 1} organizations (${startOrgId} to ${endOrgId})`);
            return { selectedOrgIds: newSelectedOrgIds };
          });
        },

        /**
         * Get selected organization IDs as array
         */
        getSelectedOrgIds: () => {
          return Array.from(get().selectedOrgIds);
        },

        // ======================================
        // Contact Selection Actions
        // ======================================

        /**
         * Select a contact by person_id
         */
        selectContact: (personId: string) => {
          set((state) => {
            const newSelectedContactIds = new Set(state.selectedContactIds);
            newSelectedContactIds.add(personId);
            logger.log(`[EPG Store] Selected contact ${personId}. Total selected: ${newSelectedContactIds.size}`);
            return { selectedContactIds: newSelectedContactIds };
          });
        },

        /**
         * Deselect a contact by person_id
         */
        deselectContact: (personId: string) => {
          set((state) => {
            const newSelectedContactIds = new Set(state.selectedContactIds);
            newSelectedContactIds.delete(personId);
            logger.log(`[EPG Store] Deselected contact ${personId}. Total selected: ${newSelectedContactIds.size}`);
            return { selectedContactIds: newSelectedContactIds };
          });
        },

        /**
         * Select all contacts in current groupings
         */
        selectAllContacts: () => {
          set((state) => {
            const allContactIds = new Set<string>();
            state.contactGroupings.forEach((group) => {
              group.contacts.forEach((contact) => {
                allContactIds.add(contact.person_id.toString());
              });
            });
            logger.log(`[EPG Store] Selected all ${allContactIds.size} contacts`);
            return { selectedContactIds: allContactIds };
          });
        },

        /**
         * Deselect all contacts
         */
        deselectAllContacts: () => {
          logger.log('[EPG Store] Deselected all contacts');
          set({ selectedContactIds: new Set<string>() });
        },

        /**
         * Set contact groupings (company bands)
         */
        setContactGroupings: (groupings: ContactGrouping[]) => {
          set({ contactGroupings: groupings });
          logger.log(`[EPG Store] Set ${groupings.length} contact groupings`);
        },

        // ======================================
        // Events Actions
        // ======================================

        /**
         * Get static fallback event options from epgConfig.ts
         * Phase 1 - Task 5: Static options fallback when API fails
         *
         * @param category - Category name
         * @returns CategoryEventOptions with static options duplicated to all months
         */
        getStaticEventOptions: (category: 'Events' | 'Calls & Emails' | 'Drops'): CategoryEventOptions => {
          const staticOptions = getCategoryOptions(category);

          const months: Record<MonthCode, EventOption[]> = {
            JAN: [], FEB: [], MAR: [], APR: [], MAY: [], JUN: [],
            JUL: [], AUG: [], SEP: [], OCT: [], NOV: [], DEC: []
          };

          // Duplicate static options to all months
          Object.keys(months).forEach((month) => {
            months[month as MonthCode] = [...staticOptions];
          });

          logger.warn(`[EPG Store] Using static fallback options for ${category} (${staticOptions.length} options)`);

          return {
            months,
            isLoading: false,
            lastLoaded: null, // null indicates fallback
          };
        },

        /**
         * Transform API response to month-based structure
         * Phase 1 - Task 2: Transform flat API response to CategoryEventOptions
         *
         * IMPORTANT: Backend returns 'name' field but frontend expects 'label'.
         * This function maps 'name' to 'label' for both flat and month-based responses.
         *
         * @param response - API response with flat event options array
         * @param category - Category name for static fallback
         * @returns CategoryEventOptions with months structure
         */
        transformEventOptions: (
          response: EventOptionsResponse | null,
          category: 'Events' | 'Calls & Emails' | 'Drops'
        ): CategoryEventOptions => {
          const months: Record<MonthCode, EventOption[]> = {
            JAN: [], FEB: [], MAR: [], APR: [], MAY: [], JUN: [],
            JUL: [], AUG: [], SEP: [], OCT: [], NOV: [], DEC: []
          };

          // Helper function to map backend event option to frontend format
          // Backend returns 'name', frontend expects 'label'
          const mapEventOption = (item: EventOption | { id: number; name?: string; [key: string]: unknown }): EventOption => ({
            id: item.id,
            label: ('name' in item && item.name) || ('label' in item && typeof item.label === 'string' ? item.label : ''),
            color: ('color' in item && typeof item.color === 'string' ? item.color : null),
            is_separator: ('is_separator' in item && typeof item.is_separator === 'boolean' ? item.is_separator : false),
            category_type: ('category_type' in item && typeof item.category_type === 'string' ? item.category_type : null),
          });

          if (!response || !response.data || response.data.length === 0) {
            // Return empty structure if no data
            return {
              months,
              isLoading: false,
              lastLoaded: new Date(),
            };
          }

          // If response already has months structure, transform it to use 'label' field
          if (response.months && Object.keys(response.months).length > 0) {
            const transformedMonths: Record<MonthCode, EventOption[]> = {
              JAN: [], FEB: [], MAR: [], APR: [], MAY: [], JUN: [],
              JUL: [], AUG: [], SEP: [], OCT: [], NOV: [], DEC: []
            };

            // Transform each month's options to map 'name' -> 'label'
            Object.entries(response.months).forEach(([month, options]) => {
              if (month in transformedMonths && Array.isArray(options)) {
                transformedMonths[month as MonthCode] = options.map(mapEventOption);
              }
            });

            return {
              months: transformedMonths,
              isLoading: false,
              lastLoaded: new Date(),
            };
          }

          // Otherwise, duplicate flat array to all months (fallback)
          // This handles API responses that return {data: [...]} without month organization
          const eventOptions: EventOption[] = response.data.map(mapEventOption);

          // Duplicate to all months (temporary - will be replaced with field-based mapping)
          Object.keys(months).forEach((month) => {
            months[month as MonthCode] = [...eventOptions];
          });

          return {
            months,
            isLoading: false,
            lastLoaded: new Date(),
          };
        },

        /**
         * Load event options for a category (Events, Calls & Emails, Drops)
         * Phase 1 - Task 3: Updated to populate eventOptionsByCategory
         */
        loadEventOptions: async (category: 'Events' | 'Calls & Emails' | 'Drops') => {
          // Normalize category name: UI uses 'Calls/Emails' but backend expects 'Calls & Emails'
          const normalizedCategory = normalizeCategoryName(category) as 'Events' | 'Calls & Emails' | 'Drops';

          // Mark category as loading
          set((state) => ({
            eventOptionsByCategory: {
              ...state.eventOptionsByCategory,
              [normalizedCategory]: {
                ...state.eventOptionsByCategory[normalizedCategory],
                isLoading: true,
              },
            },
            isLoadingEventOptions: true,
            error: null,
          }));

          try {
            logger.log(`[EPG Store] Loading event options for category: ${normalizedCategory}`);

            const result = await epgApi.getEventOptions(normalizedCategory);

            // Transform response to month-based structure
            const transformed = get().transformEventOptions(result, normalizedCategory);

            set((state) => ({
              eventOptions: result, // Keep for backward compatibility
              eventOptionsByCategory: {
                ...state.eventOptionsByCategory,
                [normalizedCategory]: transformed,
              },
              isLoadingEventOptions: false,
            }));

            logger.log(`[EPG Store] Loaded event options for ${normalizedCategory}: ${Object.values(transformed.months).reduce((sum, opts) => sum + opts.length, 0)} total options across 12 months`);
          } catch (error) {
            logger.error('[EPG Store] Failed to load event options:', error);

            // Phase 1 - Task 5: Use static fallback on API failure
            const fallbackOptions = get().getStaticEventOptions(normalizedCategory);

            set((state) => ({
              eventOptionsByCategory: {
                ...state.eventOptionsByCategory,
                [normalizedCategory]: fallbackOptions,
              },
              error: error instanceof Error ? error.message : 'Failed to load event options',
              isLoadingEventOptions: false,
            }));

            logger.warn(`[EPG Store] Loaded static fallback for ${normalizedCategory}: ${Object.values(fallbackOptions.months).reduce((sum, opts) => sum + opts.length, 0)} total options`);
          }
        },

        /**
         * Toggle event selection for a month (optimistic update with validation)
         */
        toggleEvent: (month: MonthCode, eventId: number) => {
          const { eventsByMonth, selectedOrgId, selectedOrgName, undoStack } = get();

          if (!eventsByMonth || !selectedOrgId) {
            logger.warn('[EPG Store] Cannot toggle event: no organization selected');
            return;
          }

          // Save current state for undo (limit to 50 items)
          const newUndoStack = [...undoStack, { ...eventsByMonth }].slice(-50);

          // Clone eventsByMonth
          const updatedEventsByMonth = { ...eventsByMonth };

          // Clone month data
          const monthData = { ...updatedEventsByMonth[month] };

          // Toggle event ID
          const currentIds = monthData.selected_ids || [];
          const isSelected = currentIds.includes(eventId);

          const newIds = isSelected
            ? currentIds.filter((id) => id !== eventId) // Remove
            : [...currentIds, eventId]; // Add

          // Validate max 15 events per month
          const validation = validateEventIds(newIds);
          if (!validation.valid) {
            logger.error(`[EPG Store] ${validation.error}`);
            useToastStore.getState().addToast({
              message: validation.error || 'Maximum 15 events per month exceeded',
              variant: 'error',
              duration: 7000,
            });
            return;
          }

          monthData.selected_ids = newIds;
          updatedEventsByMonth[month] = monthData;

          // Track unsaved changes
          const updates: BatchUpdateItem[] = Object.entries(updatedEventsByMonth).map(([monthCode, data]) => ({
            org_id: selectedOrgId,
            month: monthCode as MonthCode,
            event_ids: data.selected_ids,
          }));

          const unsavedChanges: EPGUnsavedChanges = {
            org_id: selectedOrgId,
            org_name: selectedOrgName || `Organization ${selectedOrgId}`,
            updates,
            timestamp: new Date().toISOString(),
          };

          set({
            eventsByMonth: updatedEventsByMonth,
            unsavedChanges,
            undoStack: newUndoStack,
            redoStack: [], // Clear redo stack on new change
          });

          logger.log(
            `[EPG Store] Toggled event ${eventId} for month ${month} (${isSelected ? 'removed' : 'added'})`
          );
        },

        /**
         * Update event assignments for a specific month (with validation)
         * Phase 2: Populates cellEdits Map to track pending changes per cell
         */
        updateMonthEvents: (month: MonthCode, eventIds: number[]) => {
          const { eventsByMonth, selectedOrgId, selectedOrgName, undoStack, cellEdits } = get();

          if (!eventsByMonth || !selectedOrgId) {
            logger.warn('[EPG Store] Cannot update month: no organization selected');
            return;
          }

          // Validate max 15 events per month
          const validation = validateEventIds(eventIds);
          if (!validation.valid) {
            logger.error(`[EPG Store] ${validation.error}`);
            set({ error: validation.error });
            return;
          }

          // Save current state for undo (limit to 50 items)
          const newUndoStack = [...undoStack, { ...eventsByMonth }].slice(-50);

          // Get original IDs before change
          const oldIds = eventsByMonth[month]?.selected_ids || [];

          // Clone eventsByMonth
          const updatedEventsByMonth = { ...eventsByMonth };

          // Clone month data and update
          const monthData = { ...updatedEventsByMonth[month] };
          monthData.selected_ids = eventIds;
          updatedEventsByMonth[month] = monthData;

          // PHASE 2: Track cell-level changes in cellEdits Map
          // Since updateMonthEvents doesn't receive category, we track all categories
          // Helper to compare arrays (order-independent)
          const arraysEqual = (a: number[], b: number[]): boolean => {
            if (a.length !== b.length) return false;
            const sortedA = [...a].sort((x, y) => x - y);
            const sortedB = [...b].sort((x, y) => x - y);
            return sortedA.every((val, idx) => val === sortedB[idx]);
          };

          const newCellEdits = new Map(cellEdits);

          // Track changes for each category separately
          const categories = ['Events', 'Calls/Emails', 'Drops'];

          categories.forEach(category => {
            const cellKey = `${selectedOrgId}-${category}-${month}`;

            // Get category-specific IDs from month data
            let categoryOriginalIds: number[] = [];
            let categoryNewIds: number[] = [];

            // Extract category-specific IDs based on naming convention
            if (category === 'Events') {
              categoryOriginalIds = eventsByMonth[month]?.events_selected_ids || [];
              categoryNewIds = monthData.events_selected_ids || [];
            } else if (category === 'Calls/Emails') {
              categoryOriginalIds = eventsByMonth[month]?.calls_emails_selected_ids || [];
              categoryNewIds = monthData.calls_emails_selected_ids || [];
            } else if (category === 'Drops') {
              categoryOriginalIds = eventsByMonth[month]?.drops_selected_ids || [];
              categoryNewIds = monthData.drops_selected_ids || [];
            }

            // Only track if there's a change from original
            if (!arraysEqual(categoryNewIds, categoryOriginalIds)) {
              newCellEdits.set(cellKey, {
                orgId: selectedOrgId,
                category,
                month,
                oldIds: categoryOriginalIds,
                newIds: categoryNewIds,
              });
            } else {
              // No change from original - remove from pending edits
              newCellEdits.delete(cellKey);
            }
          });

          // Track unsaved changes
          const updates: BatchUpdateItem[] = Object.entries(updatedEventsByMonth).map(([monthCode, data]) => ({
            org_id: selectedOrgId,
            month: monthCode as MonthCode,
            event_ids: data.selected_ids,
          }));

          const unsavedChanges: EPGUnsavedChanges = {
            org_id: selectedOrgId,
            org_name: selectedOrgName || `Organization ${selectedOrgId}`,
            updates,
            timestamp: new Date().toISOString(),
          };

          set({
            eventsByMonth: updatedEventsByMonth,
            unsavedChanges,
            undoStack: newUndoStack,
            redoStack: [], // Clear redo stack on new change
            cellEdits: newCellEdits, // PHASE 2: Update cellEdits Map
          });

          logger.log(`[EPG Store] Updated ${month} with ${eventIds.length} events (${newCellEdits.size} cell edits tracked)`);
        },

        /**
         * Update event assignments for a specific cell with cell-level tracking (Phase 3 - P2-T07)
         * This is the preferred method for updating events as it tracks oldIds vs newIds per cell
         *
         * Supports both:
         * - Grid view: Pass oldIds explicitly (from cell's current events)
         * - Single company view: Uses eventsByMonth if oldIds not provided
         */
        updateCellEvents: (orgId: number, category: string, month: MonthCode, eventIds: number[], oldIdsParam?: number[]) => {
          const { eventsByMonth, selectedOrgId, selectedOrgName, undoStack, cellEdits, organizations } = get();

          // DEBUG: Log the event update
          console.log(`[EPG Store] updateCellEvents called:`, {
            orgId,
            category,
            month,
            newEventIds: eventIds,
            oldIdsParam,
            hasEventsByMonth: !!eventsByMonth,
            selectedOrgId,
          });

          // Validate max 15 events per month
          const validation = validateEventIds(eventIds);
          if (!validation.valid) {
            logger.error(`[EPG Store] ${validation.error}`);
            set({ error: validation.error });
            return;
          }

          // Get original IDs - priority:
          // 1. Passed oldIds (grid view)
          // 2. Existing cell edit (for updates to pending changes)
          // 3. eventsByMonth category-specific array (single company view)
          // IMPORTANT: Must use category-specific arrays to avoid phantom removal events
          const cellKey = `${orgId}-${category}-${month}`;
          const existingCellEdit = cellEdits.get(cellKey);

          // FIX: Get category-specific original IDs instead of combined selected_ids
          // This prevents phantom "removed" events from appearing in the wrong category
          const normalizedCat = category === 'Calls & Emails' ? 'Calls/Emails' : category;
          let categoryOldIds: number[] = [];
          if (eventsByMonth?.[month]) {
            const monthData = eventsByMonth[month];
            if (normalizedCat === 'Events') {
              categoryOldIds = monthData.events_selected_ids || [];
            } else if (normalizedCat === 'Calls/Emails') {
              categoryOldIds = monthData.calls_emails_selected_ids || [];
            } else if (normalizedCat === 'Drops') {
              categoryOldIds = monthData.drops_selected_ids || [];
            }
          }
          const oldIds = oldIdsParam ?? existingCellEdit?.oldIds ?? categoryOldIds;

          // DEBUG: Log the resolved oldIds
          console.log(`[EPG Store] updateCellEvents resolved oldIds:`, {
            cellKey,
            oldIds,
            source: oldIdsParam ? 'param' : existingCellEdit ? 'cellEdit' : 'eventsByMonth',
          });

          // Get organization name for display
          const org = organizations.find(o => o.id === orgId);
          const displayOrgName = selectedOrgName || org?.name || `Organization ${orgId}`;

          // Track cell-level changes
          const newCellEdits = new Map(cellEdits);

          // Only track if there's an actual change from original
          const hasChanged = JSON.stringify([...oldIds].sort()) !== JSON.stringify([...eventIds].sort());
          if (hasChanged) {
            newCellEdits.set(cellKey, {
              orgId,
              category,
              month,
              oldIds,
              newIds: eventIds,
            });
          } else {
            // If reverting to original, remove from cellEdits
            newCellEdits.delete(cellKey);
          }

          // Update state based on view mode
          if (eventsByMonth && selectedOrgId === orgId) {
            // Single company view mode - update eventsByMonth
            const newUndoStack = [...undoStack, { ...eventsByMonth }].slice(-50);
            const updatedEventsByMonth = { ...eventsByMonth };
            const monthData = { ...updatedEventsByMonth[month] };

            // FIX: Update category-specific array to prevent phantom events in other categories
            // Normalize category name for comparison
            const normalizedCategory = category === 'Calls & Emails' ? 'Calls/Emails' : category;
            if (normalizedCategory === 'Events') {
              monthData.events_selected_ids = eventIds;
            } else if (normalizedCategory === 'Calls/Emails') {
              monthData.calls_emails_selected_ids = eventIds;
            } else if (normalizedCategory === 'Drops') {
              monthData.drops_selected_ids = eventIds;
            }
            // Also update combined selected_ids for backward compatibility
            // This merges all category IDs into one array
            const allIds = new Set([
              ...(monthData.events_selected_ids || []),
              ...(monthData.calls_emails_selected_ids || []),
              ...(monthData.drops_selected_ids || []),
            ]);
            monthData.selected_ids = Array.from(allIds);

            updatedEventsByMonth[month] = monthData;

            // Track unsaved changes
            const updates: BatchUpdateItem[] = Object.entries(updatedEventsByMonth).map(([monthCode, data]) => ({
              org_id: selectedOrgId,
              month: monthCode as MonthCode,
              event_ids: data.selected_ids,
            }));

            const unsavedChanges: EPGUnsavedChanges = {
              org_id: selectedOrgId,
              org_name: displayOrgName,
              updates,
              timestamp: new Date().toISOString(),
            };

            set({
              eventsByMonth: updatedEventsByMonth,
              unsavedChanges,
              undoStack: newUndoStack,
              redoStack: [],
              cellEdits: newCellEdits,
            });
          } else {
            // Grid view mode - just update cellEdits (no eventsByMonth update needed)
            set({
              cellEdits: newCellEdits,
            });
          }

          logger.log(
            `[EPG Store] Updated cell ${cellKey}: ${oldIds.length} -> ${eventIds.length} events`
          );

          // Save to backend (Phase 6) - debounced to avoid excessive API calls
          if (hasChanged) {
            // Capture orgName for closure
            const orgNameForBackend = displayOrgName;
            // Use setTimeout for simple debouncing (300ms)
            setTimeout(() => {
              get().savePendingChangeToBackend(orgId, orgNameForBackend, category, month, oldIds, eventIds);
            }, 300);
          }
        },

        /**
         * Clear events for a category across visible months only (bullet click row-wide removal)
         * BUGFIX (P1-T2): Only clears visible months, not all 12 months
         * Marks events in the category row as pending removal (empty newIds)
         *
         * @param visibleMonthKeys - Optional array of visible month codes. If not provided, defaults to all 12 months for backward compatibility.
         */
        clearCategoryEvents: (orgId: number, category: string, visibleMonthKeys?: MonthCode[]) => {
          const { cellEdits, organizations, viewPreferences, selectedOrgYear } = get();

          // BUGFIX (P1-T2): Use only visible months if provided, otherwise fall back to all 12 months
          let monthCodes: MonthCode[];
          if (visibleMonthKeys && visibleMonthKeys.length > 0) {
            monthCodes = visibleMonthKeys;
            logger.log(`[EPG Store] clearCategoryEvents: Clearing ${monthCodes.length} visible months for ${category}`);
          } else {
            // Fallback: Calculate visible months from viewPreferences
            const allMonthKeys: MonthCode[] = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
            const startMonth = viewPreferences.startMonth || 0;
            const visibleCount = viewPreferences.visibleMonthCount || 12;
            const safeStart = Math.min(startMonth, 12 - visibleCount);
            monthCodes = allMonthKeys.slice(safeStart, safeStart + visibleCount);
            logger.log(`[EPG Store] clearCategoryEvents: No visible months provided, using viewPreferences (${monthCodes.length} months from ${monthCodes[0]})`);
          }

          // Get organization name for display
          const org = organizations.find(o => o.id === orgId);
          const orgName = org?.name || `Organization ${orgId}`;

          // Get org's events from organization object (Pipedrive field mapping)
          const orgData = org as EPGOrganization & Record<string, unknown>;

          const newCellEdits = new Map(cellEdits);
          let changesCount = 0;

          // Get the correct field hash mapping based on category
          const getFieldHash = (monthCode: MonthCode): string | undefined => {
            if (category === 'Events') return EVENT_FIELD_HASHES[monthCode];
            if (category === 'Drops') return DROPS_FIELD_HASHES[monthCode];
            if (category === 'Calls/Emails') return CALLS_EMAILS_FIELD_HASHES[monthCode];
            return undefined;
          };

          // Parse Pipedrive field value to array of IDs
          const parseFieldValue = (value: unknown): number[] => {
            if (!value) return [];
            if (Array.isArray(value)) {
              return value.map(v => typeof v === 'string' ? parseInt(v, 10) : v).filter(v => !isNaN(v));
            }
            if (typeof value === 'string') {
              if (value.includes(',')) {
                return value.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
              }
              const parsed = parseInt(value, 10);
              return isNaN(parsed) ? [] : [parsed];
            }
            if (typeof value === 'number') return [value];
            return [];
          };

          console.log(`[EPG Store] clearCategoryEvents called for org ${orgId}, category ${category}`);
          if (!org) {
            console.warn(`[EPG Store] Organization ${orgId} not found in store!`);
            return;
          }
          console.log(`[EPG Store] Organization found:`, org.name);
          console.log(`[EPG Store] Organization data keys (first 20):`, Object.keys(orgData || {}).slice(0, 20));
          const fieldHashKeys = Object.keys(orgData || {}).filter(k => k.length === 40);
          console.log(`[EPG Store] Field hash keys count:`, fieldHashKeys.length, fieldHashKeys.slice(0, 3));
          logger.log(`[EPG Store] clearCategoryEvents called for org ${orgId}, category ${category}`);

          // For each month, create a pending change that removes all events for this category
          monthCodes.forEach(month => {
            const cellKey = `${orgId}-${category}-${month}`;

            // Get current IDs using the correct Pipedrive field hash
            const fieldHash = getFieldHash(month);
            const fieldValue = fieldHash ? orgData?.[fieldHash] : undefined;
            const currentIds = parseFieldValue(fieldValue);

            // Debug logging for first few months
            if (month === 'JAN' || month === 'FEB') {
              console.log(`[EPG Store] ${month} - fieldHash: ${fieldHash?.substring(0, 10)}..., fieldValue:`, fieldValue, `currentIds:`, currentIds);
            }

            // Check if there's an existing pending change - use that as baseline
            const existingEdit = cellEdits.get(cellKey);
            if (existingEdit) {
              // If already has pending changes, use oldIds as baseline
              if (existingEdit.newIds.length > 0) {
                // Mark for removal - set newIds to empty
                newCellEdits.set(cellKey, {
                  ...existingEdit,
                  newIds: [], // All removed
                });
                changesCount++;
              }
              // If already empty, nothing to do
            } else if (currentIds.length > 0) {
              // No pending change but has events - create new pending removal
              newCellEdits.set(cellKey, {
                orgId,
                category,
                month,
                oldIds: currentIds,
                newIds: [], // All removed
              });
              changesCount++;
            }
          });

          console.log(`[EPG Store] clearCategoryEvents found ${changesCount} months with events to clear`);

          if (changesCount > 0) {
            set({ cellEdits: newCellEdits });
            console.log(`[EPG Store] Cleared ${category} for org ${orgId}: ${changesCount} months affected, cellEdits size: ${newCellEdits.size}`);
            logger.log(`[EPG Store] Cleared ${category} for org ${orgId}: ${changesCount} months affected, cellEdits size: ${newCellEdits.size}`);

            // Save each pending change to backend
            monthCodes.forEach(month => {
              const cellKey = `${orgId}-${category}-${month}`;
              const cellEdit = newCellEdits.get(cellKey);
              if (cellEdit && cellEdit.oldIds.length > 0) {
                setTimeout(() => {
                  get().savePendingChangeToBackend(orgId, orgName, category, month, cellEdit.oldIds, []);
                }, 300);
              }
            });
          }
        },

        /**
         * Discard specific cell edits (Phase 4 - Enhanced PendingChangesDialog)
         * Reverts selected cells to their original state
         * BUGFIX (P1-T3): Added grid refresh to update visual state after discard
         */
        discardCellEdits: (cellKeys: string[]) => {
          const { eventsByMonth, cellEdits, selectedOrgId, undoStack } = get();

          // Don't require selectedOrgId or eventsByMonth
          // This allows discarding changes for multiple organizations in grid view
          // Only eventsByMonth update requires selectedOrgId (for single company view)

          const newCellEdits = new Map(cellEdits);

          // Remove specified cell edits from the Map
          cellKeys.forEach((cellKey) => {
            newCellEdits.delete(cellKey);
          });

          // Update state based on context
          if (eventsByMonth && selectedOrgId) {
            // Single company view mode - update eventsByMonth and unsavedChanges
            const newUndoStack = [...undoStack, { ...eventsByMonth }].slice(-50);
            const updatedEventsByMonth = { ...eventsByMonth };

            // Revert each cell to original state in eventsByMonth
            cellKeys.forEach((cellKey) => {
              const cellEdit = cellEdits.get(cellKey);
              if (cellEdit && cellEdit.orgId === selectedOrgId) {
                // Only revert if it's for the currently selected organization
                const monthData = { ...updatedEventsByMonth[cellEdit.month] };
                monthData.selected_ids = cellEdit.oldIds;
                updatedEventsByMonth[cellEdit.month] = monthData;
              }
            });

            // Update unsaved changes (or clear if no more edits for selected org)
            let unsavedChanges: EPGUnsavedChanges | null = null;
            const remainingEditsForOrg = Array.from(newCellEdits.values()).some(edit => edit.orgId === selectedOrgId);
            if (remainingEditsForOrg) {
              const updates: BatchUpdateItem[] = Object.entries(updatedEventsByMonth).map(([monthCode, data]) => ({
                org_id: selectedOrgId,
                month: monthCode as MonthCode,
                event_ids: data.selected_ids,
              }));

              unsavedChanges = {
                org_id: selectedOrgId,
                org_name: get().selectedOrgName || `Organization ${selectedOrgId}`,
                updates,
                timestamp: new Date().toISOString(),
              };
            }

            set({
              eventsByMonth: updatedEventsByMonth,
              unsavedChanges,
              undoStack: newUndoStack,
              redoStack: [],
              cellEdits: newCellEdits,
            });
          } else {
            // Grid view mode - update cellEdits Map and reset cache to force grid refresh
            set({
              cellEdits: newCellEdits,
              lastOrganizationsFetchTime: null, // Force refresh on next loadOrganizations()
            });

            // BUGFIX (P1-T3): Refresh organizations list to update grid visual state
            // This ensures discarded cells revert to their original appearance
            get().loadOrganizations().catch((error) => {
              logger.error('[EPG Store] Failed to refresh grid after discard:', error);
            });
          }

          // Delete discarded changes from backend
          cellKeys.forEach((cellKey) => {
            const cellEdit = cellEdits.get(cellKey);
            if (cellEdit) {
              // Async delete from backend (fire and forget - don't block UI)
              epgApi.deletePendingChange(cellEdit.orgId, cellEdit.category, cellEdit.month).catch((error) => {
                logger.error(`[EPG Store] Failed to delete pending change from backend: ${cellKey}`, error);
              });
            }
          });

          logger.log(`[EPG Store] Discarded ${cellKeys.length} cell edit(s)`);
        },

        /**
         * Commit all pending changes to Pipedrive via batch update
         */
        commitSync: async () => {
          const { unsavedChanges, selectedOrgId, cellEdits } = get();

          if (!unsavedChanges && cellEdits.size === 0) {
            logger.warn('[EPG Store] No unsaved changes to commit');
            useToastStore.getState().addToast({
              message: 'No changes to sync',
              variant: 'info',
              duration: 3000,
            });
            return;
          }

          // If we have cellEdits but no unsavedChanges, convert cellEdits to updates
          let updates = unsavedChanges?.updates || [];

          if (cellEdits.size > 0 && !unsavedChanges) {
            // Convert cellEdits to updates format - IMPORTANT: Use org_id not organization_id
            updates = Array.from(cellEdits.values()).map(edit => ({
              org_id: edit.orgId,  // Backend expects org_id, not organization_id
              category: edit.category,
              month: edit.month,
              event_ids: edit.newIds,
            }));

            if (!selectedOrgId && updates.length > 0) {
              // Use the first organization from cellEdits if no selectedOrgId
              const firstEdit = cellEdits.values().next().value;
              if (firstEdit) {
                set({ selectedOrgId: firstEdit.orgId });
              }
            }
          }

          const orgId = selectedOrgId || (updates.length > 0 ? updates[0].org_id : null);

          if (!orgId || updates.length === 0) {
            logger.warn('[EPG Store] No valid changes to commit');
            return;
          }

          set({ isSyncing: true, isSaving: true, error: null });

          try {
            logger.log(`[EPG Store] Committing ${updates.length} changes for org ${orgId}`);

            // Generate push_id for progress tracking
            const pushId = `push_${Date.now()}`;

            // Store total in sessionStorage for frontend progress tracking
            sessionStorage.setItem(`push_${pushId}_total`, String(updates.length));

            const result = await epgApi.batchUpdate({
              updates: updates,
              sync_to_pipedrive: true, // Auto-sync to Pipedrive
            }, pushId);

            // Check for failures
            if (result.failed > 0) {
              const errorMsg = `${result.failed} update(s) failed: ${result.errors.join(', ')}`;
              logger.error(`[EPG Store] ${errorMsg}`);
              set({ error: errorMsg });

              // Show partial success/failure toast
              useToastStore.getState().addToast({
                message: `Sync completed: ${result.successful} succeeded, ${result.failed} failed`,
                variant: 'warning',
                duration: 7000,
              });

              // Still throw error to keep dialog open for retry
              throw new Error(errorMsg);
            }

            // Reload organization events to get fresh data from backend
            if (selectedOrgId) {
              await get().selectOrganization(selectedOrgId);
            }

            set({
              unsavedChanges: null, // Clear unsaved changes
              cellEdits: new Map(), // Clear cell-level edits
              isSyncing: false,
              isSaving: false,
            });

            logger.log(
              `[EPG Store] Committed ${result.successful} changes for org ${orgId}`
            );

            // Return success for caller to handle
            return { successful: result.successful, failed: result.failed };
          } catch (error) {
            logger.error('[EPG Store] Failed to commit changes:', error);
            set({
              error: error instanceof Error ? error.message : 'Failed to commit changes',
              isSyncing: false,
              isSaving: false,
            });
            throw error; // Re-throw for UI error handling
          }
        },

        /**
         * Alias for commitSync (used by toolbar UI)
         */
        saveChanges: async () => {
          return get().commitSync();
        },

        /**
         * Discard all unsaved changes (revert to last saved state)
         */
        discardChanges: () => {
          const { selectedOrgId } = get();

          if (!selectedOrgId) {
            logger.warn('[EPG Store] Cannot discard: no organization selected');
            return;
          }

          logger.log(`[EPG Store] Discarding changes for org ${selectedOrgId}`);

          // Reload events from backend to revert
          get().selectOrganization(selectedOrgId);
        },

        /**
         * Refresh data from Pipedrive (Pull operation - Phase 5 - P5-T02)
         * Warns if there are unsaved changes before reloading
         * Uses force_refresh flag to bypass cache and get fresh data from Pipedrive
         */
        refreshFromPipedrive: async () => {
          const { selectedOrgId, unsavedChanges } = get();

          if (!selectedOrgId) {
            logger.warn('[EPG Store] Cannot refresh: no organization selected');
            useToastStore.getState().addToast({
              message: 'No organization selected',
              variant: 'error',
              duration: 5000,
            });
            return;
          }

          // Warn about unsaved changes
          if (unsavedChanges) {
            const confirmed = confirm(
              `You have ${unsavedChanges.updates.length} unsaved change(s). Refreshing will discard these changes. Continue?`
            );
            if (!confirmed) {
              logger.log('[EPG Store] Refresh cancelled by user');
              return;
            }
          }

          set({ isSyncing: true, isSaving: true, error: null });

          try {
            logger.log(`[EPG Store] Pulling fresh data from Pipedrive for org ${selectedOrgId}`);

            // Use pullFromPipedrive API method with force_refresh flag
            const freshEvents = await epgApi.pullFromPipedrive(selectedOrgId);

            set({
              eventsByMonth: freshEvents.events_by_month,
              unsavedChanges: null,
              undoStack: [],
              redoStack: [],
              isSyncing: false,
              isSaving: false,
            });

            useToastStore.getState().addToast({
              message: 'Successfully refreshed data from Pipedrive',
              variant: 'success',
              duration: 5000,
            });

            logger.log('[EPG Store] Pull from Pipedrive complete');
          } catch (error) {
            logger.error('[EPG Store] Failed to pull from Pipedrive:', error);
            set({
              error: error instanceof Error ? error.message : 'Failed to refresh from Pipedrive',
              isSyncing: false,
              isSaving: false,
            });

            useToastStore.getState().addToast({
              message: error instanceof Error ? error.message : 'Failed to refresh from Pipedrive',
              variant: 'error',
              duration: 7000,
            });
          }
        },

        /**
         * Push changes to Pipedrive (Phase 5 - P5-T01)
         * Shows confirmation dialog if more than 10 changes
         * Displays progress indicator during sync
         */
        pushToPipedrive: async () => {
          const { cellEdits, organizations } = get();

          // Check for pending changes in cellEdits Map
          if (cellEdits.size === 0) {
            logger.warn('[EPG Store] No pending changes to push');
            useToastStore.getState().addToast({
              message: 'No pending changes to push',
              variant: 'warning',
              duration: 5000,
            });
            return;
          }

          // Convert cellEdits Map to batch update format
          const updates: BatchUpdateItem[] = [];
          cellEdits.forEach((edit) => {
            updates.push({
              org_id: edit.orgId,
              month: edit.month,
              event_ids: edit.newIds,
              category: edit.category, // Include category field required by backend
            });
          });

          const changeCount = cellEdits.size;

          // Confirmation dialog for >10 changes
          if (changeCount > 10) {
            const confirmed = confirm(
              `You are about to push ${changeCount} changes to Pipedrive. This cannot be undone. Continue?`
            );
            if (!confirmed) {
              logger.log('[EPG Store] Push cancelled by user');
              return;
            }
          }

          set({ isSyncing: true, isSaving: true, error: null });

          try {
            logger.log(`[EPG Store] Pushing ${changeCount} changes to Pipedrive`);

            const result = await epgApi.pushToPipedrive({
              updates: updates,
              sync_to_pipedrive: true,
            });

            // Check for failures
            if (result.failed > 0) {
              const errorMsg = `${result.failed} updates failed: ${result.errors.join(', ')}`;
              logger.error(`[EPG Store] ${errorMsg}`);
              set({ error: errorMsg });

              useToastStore.getState().addToast({
                message: `Pushed ${result.successful} changes, but ${result.failed} failed`,
                variant: 'warning',
                duration: 7000,
              });
            } else {
              useToastStore.getState().addToast({
                message: `Successfully pushed ${result.successful} changes to Pipedrive`,
                variant: 'success',
                duration: 5000,
              });
            }

            // Clear pending changes after successful push
            // Reset lastOrganizationsFetchTime to force grid refresh (bypass cache)
            set({
              unsavedChanges: null,
              cellEdits: new Map(),
              isSyncing: false,
              isSaving: false,
              lastOrganizationsFetchTime: null,
            });

            // Refresh organizations list for spreadsheet grid view
            // This ensures the grid shows updated data after successful sync
            await get().loadOrganizations();

            // If we have a selected organization, also reload its data for single-company view
            const { selectedOrgId } = get();
            if (selectedOrgId) {
              await get().selectOrganization(selectedOrgId);
            }

            // Note: Backend should automatically clear pending changes after successful push
            // No need to explicitly delete them

            logger.log(
              `[EPG Store] Push completed: ${result.successful} succeeded, ${result.failed} failed`
            );
          } catch (error) {
            logger.error('[EPG Store] Failed to push to Pipedrive:', error);
            set({
              error: error instanceof Error ? error.message : 'Failed to push to Pipedrive',
              isSyncing: false,
              isSaving: false,
            });

            useToastStore.getState().addToast({
              message: error instanceof Error ? error.message : 'Failed to push to Pipedrive',
              variant: 'error',
              duration: 7000,
            });

            throw error; // Re-throw for UI error handling
          }
        },

        // ======================================
        // Field Definitions Actions (Pipedrive Parity)
        // ======================================

        /**
         * Load cached field definitions from backend
         *
         * Fetches field definitions for Pipedrive custom fields.
         * Supports optional category filtering.
         */
        loadFieldDefinitions: async (category?: string) => {
          set({ isLoadingFieldDefinitions: true, error: null });

          try {
            // Normalize category name if provided: UI uses 'Calls/Emails' but backend expects 'Calls & Emails'
            const normalizedCategory = category ? normalizeCategoryName(category) : undefined;
            logger.log(`[EPG Store] Loading field definitions (category=${normalizedCategory || 'all'})`);

            const result = await epgApi.getFieldDefinitions(normalizedCategory);

            set({
              fieldDefinitions: result.fields,
              fieldDefinitionsCategories: result.categories,
              fieldDefinitionsLastSync: result.last_sync,
              isLoadingFieldDefinitions: false,
            });

            logger.log(
              `[EPG Store] Loaded ${Object.keys(result.fields).length} field definitions`
            );
          } catch (error) {
            logger.error('[EPG Store] Failed to load field definitions:', error);
            set({
              error: error instanceof Error ? error.message : 'Failed to load field definitions',
              isLoadingFieldDefinitions: false,
            });

            useToastStore.getState().addToast({
              message: error instanceof Error ? error.message : 'Failed to load field definitions',
              variant: 'error',
              duration: 5000,
            });
          }
        },

        /**
         * Sync field definitions from Pipedrive API
         *
         * Refreshes the cached field definitions by fetching from Pipedrive.
         * Use sparingly as this makes an API call to Pipedrive.
         */
        syncFieldDefinitions: async () => {
          set({ isSyncingFieldDefinitions: true, error: null });

          try {
            logger.log('[EPG Store] Syncing field definitions from Pipedrive');

            const result = await epgApi.syncFieldDefinitions();

            // Check for errors
            if (result.errors && result.errors.length > 0) {
              const errorMsg = `Sync completed with errors: ${result.errors.join(', ')}`;
              logger.error(`[EPG Store] ${errorMsg}`);
              set({ error: errorMsg });

              useToastStore.getState().addToast({
                message: `Synced ${result.fields_updated} fields, but ${result.errors.length} errors occurred`,
                variant: 'warning',
                duration: 7000,
              });
            } else {
              useToastStore.getState().addToast({
                message: `Successfully synced ${result.fields_updated} field definitions from Pipedrive`,
                variant: 'success',
                duration: 5000,
              });
            }

            // Reload field definitions to get the fresh data
            await get().loadFieldDefinitions();

            set({
              isSyncingFieldDefinitions: false,
            });

            logger.log(`[EPG Store] Synced ${result.fields_updated} fields from Pipedrive`);
          } catch (error) {
            logger.error('[EPG Store] Failed to sync field definitions:', error);
            set({
              error: error instanceof Error ? error.message : 'Failed to sync field definitions',
              isSyncingFieldDefinitions: false,
            });

            useToastStore.getState().addToast({
              message: error instanceof Error ? error.message : 'Failed to sync field definitions',
              variant: 'error',
              duration: 7000,
            });
          }
        },

        // ======================================
        // Pending Changes Actions (Phase 6)
        // ======================================

        /**
         * Load pending changes from backend on init
         * Populates cellEdits Map from backend storage
         */
        loadPendingChanges: async () => {
          try {
            logger.log('[EPG Store] Loading pending changes from backend');

            const changes = await epgApi.getPendingChanges();

            // Convert to cellEdits Map
            const newCellEdits = new Map<string, { orgId: number; category: string; month: MonthCode; oldIds: number[]; newIds: number[] }>();
            changes.forEach((change) => {
              const cellKey = `${change.orgId}-${change.category}-${change.month}`;
              newCellEdits.set(cellKey, {
                orgId: change.orgId,
                category: change.category,
                month: change.month,
                oldIds: change.oldIds,
                newIds: change.newIds,
              });
            });

            set({ cellEdits: newCellEdits });

            logger.log(`[EPG Store] Loaded ${changes.length} pending changes from backend`);
          } catch (error) {
            logger.error('[EPG Store] Failed to load pending changes:', error);
            // Don't throw - just log the error and continue
            set({ error: error instanceof Error ? error.message : 'Failed to load pending changes' });
          }
        },

        /**
         * Save pending change to backend (called after local cellEdit update)
         * Uses debouncing (300ms) to avoid excessive API calls
         */
        savePendingChangeToBackend: async (
          orgId: number,
          orgName: string,
          category: string,
          month: MonthCode,
          oldIds: number[],
          newIds: number[]
        ) => {
          try {
            // Normalize category name: UI uses 'Calls/Emails' but backend expects 'Calls & Emails'
            const normalizedCategory = normalizeCategoryName(category);
            logger.log(`[EPG Store] Saving pending change to backend: ${orgId}-${normalizedCategory}-${month}`);

            const change: PendingChange = {
              orgId,
              orgName,
              category: normalizedCategory,
              month,
              oldIds,
              newIds,
            };

            await epgApi.savePendingChange(change);

            logger.log('[EPG Store] Saved pending change to backend');
          } catch (error) {
            logger.error('[EPG Store] Failed to save pending change:', error);
            // Don't throw - continue with local state
            useToastStore.getState().addToast({
              message: 'Warning: Failed to persist change to backend',
              variant: 'warning',
              duration: 5000,
            });
          }
        },

        /**
         * Discard a specific pending change (delete from backend + local)
         */
        discardPendingChange: async (orgId: number, category: string, month: MonthCode) => {
          try {
            // Normalize category name for backend API call
            const normalizedCategory = normalizeCategoryName(category);
            logger.log(`[EPG Store] Discarding pending change: ${orgId}-${normalizedCategory}-${month}`);

            // Delete from backend (use normalized category)
            await epgApi.deletePendingChange(orgId, normalizedCategory, month);

            // Remove from local cellEdits Map
            const cellKey = `${orgId}-${category}-${month}`;
            set((state) => {
              const newCellEdits = new Map(state.cellEdits);
              newCellEdits.delete(cellKey);
              return { cellEdits: newCellEdits };
            });

            logger.log('[EPG Store] Discarded pending change');

            useToastStore.getState().addToast({
              message: 'Pending change discarded',
              variant: 'success',
              duration: 3000,
            });
          } catch (error) {
            logger.error('[EPG Store] Failed to discard pending change:', error);
            useToastStore.getState().addToast({
              message: error instanceof Error ? error.message : 'Failed to discard pending change',
              variant: 'error',
              duration: 5000,
            });
            throw error;
          }
        },

        /**
         * Discard all pending changes (clear all from backend + local)
         * BUGFIX (P1-T3): Added organization list refresh to update grid visual state
         */
        discardAllPendingChanges: async () => {
          try {
            logger.log('[EPG Store] Discarding all pending changes');

            // Delete all from backend
            await epgApi.deleteAllPendingChanges();

            // Clear local cellEdits Map and reset cache timestamp to force refresh
            set({
              cellEdits: new Map(),
              lastOrganizationsFetchTime: null, // Force refresh on next loadOrganizations()
            });

            // Refresh organizations list to update grid visual state
            // This ensures grid cells revert to their original state (no red strikethrough)
            await get().loadOrganizations();

            logger.log('[EPG Store] Discarded all pending changes and refreshed grid');

            useToastStore.getState().addToast({
              message: 'All pending changes discarded',
              variant: 'success',
              duration: 3000,
            });
          } catch (error) {
            logger.error('[EPG Store] Failed to discard all pending changes:', error);
            useToastStore.getState().addToast({
              message: error instanceof Error ? error.message : 'Failed to discard all pending changes',
              variant: 'error',
              duration: 5000,
            });
            throw error;
          }
        },

        /**
         * Clear pending changes for deleted organizations
         * Checks each organization with pending changes against Pipedrive API
         * and removes pending changes for organizations that have been deleted.
         */
        clearDeletedOrgChanges: async () => {
          try {
            logger.log('[EPG Store] Clearing pending changes for deleted organizations');

            const result = await epgApi.clearDeletedOrgChanges();

            // Remove cleared organizations from local cellEdits Map
            if (result.cleared_orgs.length > 0) {
              const clearedOrgIds = result.cleared_orgs.map(org => org.org_id);
              const newCellEdits = new Map(get().cellEdits);

              // Remove all cell edits for cleared organizations
              for (const [key, value] of newCellEdits.entries()) {
                if (clearedOrgIds.includes(value.orgId)) {
                  newCellEdits.delete(key);
                }
              }

              set({ cellEdits: newCellEdits });

              logger.log(`[EPG Store] Cleared pending changes for ${result.cleared_orgs.length} deleted organizations`);

              const orgNames = result.cleared_orgs.map(org => org.org_name).join(', ');
              useToastStore.getState().addToast({
                message: `Cleared pending changes for deleted organizations: ${orgNames}`,
                variant: 'success',
                duration: 5000,
              });

              // Reload organizations to refresh the display
              await get().loadOrganizations();
            } else {
              logger.log('[EPG Store] No deleted organizations found with pending changes');
              useToastStore.getState().addToast({
                message: 'No deleted organizations found with pending changes',
                variant: 'info',
                duration: 3000,
              });
            }
          } catch (error) {
            logger.error('[EPG Store] Failed to clear deleted organization changes:', error);
            useToastStore.getState().addToast({
              message: error instanceof Error ? error.message : 'Failed to clear deleted organization changes',
              variant: 'error',
              duration: 5000,
            });
            throw error;
          }
        },

        // ======================================
        // Contact Event Type Edit Actions
        // ======================================

        /**
         * Toggle a contact's event type selection
         * Creates or updates a ContactEventEdit in the contactEdits Map
         */
        toggleContactEventType: (
          contact: { id: number; name: string; org_id: number | null; organization_name?: string },
          eventType: string
        ) => {
          const { contactEdits, organizationContacts } = get();
          const newContactEdits = new Map(contactEdits);

          // Find the contact from organizationContacts to get current event types
          const fullContact = organizationContacts.find(c => c.id === contact.id);

          // Get existing edit or create new one
          const existingEdit = newContactEdits.get(contact.id);

          // Determine original event types (from Pipedrive data or existing edit)
          let originalEventTypes: string[] = [];
          if (existingEdit) {
            originalEventTypes = existingEdit.originalEventTypes;
          } else if (fullContact) {
            // Build from boolean fields
            CONTACT_EVENT_TYPES.forEach(et => {
              if ((fullContact as Record<string, unknown>)[et.key] === true) {
                originalEventTypes.push(et.fullLabel);
              }
            });
          }

          // Determine current event types (from edit or original)
          let currentEventTypes = existingEdit
            ? [...existingEdit.newEventTypes]
            : [...originalEventTypes];

          // Toggle the event type
          const eventIndex = currentEventTypes.indexOf(eventType);
          if (eventIndex >= 0) {
            currentEventTypes.splice(eventIndex, 1);
          } else {
            currentEventTypes.push(eventType);
          }

          // Check if we're back to original state
          const originalSet = new Set(originalEventTypes);
          const currentSet = new Set(currentEventTypes);
          const isUnchanged = originalSet.size === currentSet.size &&
            [...originalSet].every(item => currentSet.has(item));

          if (isUnchanged) {
            // Remove from pending edits
            newContactEdits.delete(contact.id);
            logger.log(`[EPG Store] Contact ${contact.id} reverted to original state, removed from pending edits`);
          } else {
            // Create or update the edit
            newContactEdits.set(contact.id, {
              contactId: contact.id,
              contactName: contact.name,
              orgId: contact.org_id || 0,
              orgName: contact.organization_name || 'Unknown',
              originalEventTypes,
              newEventTypes: currentEventTypes,
              timestamp: Date.now(),
            });
            logger.log(`[EPG Store] Contact ${contact.id} event types updated:`, currentEventTypes);
          }

          set({ contactEdits: newContactEdits });
        },

        /**
         * Discard a single contact edit
         */
        discardContactEdit: (contactId: number) => {
          const newContactEdits = new Map(get().contactEdits);
          newContactEdits.delete(contactId);
          set({ contactEdits: newContactEdits });
          logger.log(`[EPG Store] Discarded contact edit for contact ${contactId}`);
        },

        /**
         * Discard all contact edits
         */
        discardAllContactEdits: () => {
          set({ contactEdits: new Map() });
          logger.log('[EPG Store] Discarded all contact edits');
          useToastStore.getState().addToast({
            message: 'All contact changes discarded',
            variant: 'success',
            duration: 3000,
          });
        },

        /**
         * Push contact changes to Pipedrive
         * Sends all pending contactEdits to the backend
         */
        pushContactChanges: async () => {
          const { contactEdits } = get();

          if (contactEdits.size === 0) {
            logger.warn('[EPG Store] No contact changes to push');
            return;
          }

          set({ isSyncing: true, isSaving: true });

          try {
            logger.log(`[EPG Store] Pushing ${contactEdits.size} contact changes to Pipedrive`);

            // Convert contactEdits Map to array of changes
            const changes = Array.from(contactEdits.values()).map(edit => ({
              contact_id: edit.contactId,
              event_types: edit.newEventTypes.join(', '),
            }));

            // Call backend API
            const response = await epgApi.pushContactChanges(changes);

            // Handle partial success - remove only successfully updated contacts
            const successfulIds = new Set(
              response.results
                .filter(r => r.success)
                .map(r => r.contact_id)
            );

            // Create new map with only failed contacts (for retry)
            const remainingEdits = new Map(
              Array.from(contactEdits.entries()).filter(
                ([contactId]) => !successfulIds.has(contactId)
              )
            );

            // Calculate counts
            const successCount = successfulIds.size;
            const failCount = contactEdits.size - successCount;

            // Update state - clear successful edits, keep failed ones
            set({ contactEdits: remainingEdits });

            // Show appropriate toast based on results
            if (failCount === 0) {
              // All succeeded
              useToastStore.getState().addToast({
                message: `Updated ${successCount} contact(s) successfully`,
                variant: 'success',
                duration: 3000,
              });
              logger.log('[EPG Store] Contact changes pushed successfully');
            } else if (successCount === 0) {
              // All failed
              useToastStore.getState().addToast({
                message: response.error || `Failed to update ${failCount} contact(s)`,
                variant: 'error',
                duration: 5000,
              });
              logger.error('[EPG Store] All contact changes failed');
              // Throw to signal error to UI
              throw new Error(response.error || `Failed to update ${failCount} contact(s)`);
            } else {
              // Partial success
              useToastStore.getState().addToast({
                message: `Updated ${successCount} contact(s), ${failCount} failed (retry available)`,
                variant: 'warning',
                duration: 5000,
              });
              logger.warn(`[EPG Store] Partial success: ${successCount} succeeded, ${failCount} failed`);
              // Don't throw - partial success is still progress
            }
          } catch (error) {
            logger.error('[EPG Store] Failed to push contact changes:', error);
            // Only show toast if not already shown above
            if (!(error instanceof Error && error.message.includes('Failed to update'))) {
              useToastStore.getState().addToast({
                message: error instanceof Error ? error.message : 'Failed to push contact changes',
                variant: 'error',
                duration: 5000,
              });
            }
            throw error; // Re-throw for UI to handle
          } finally {
            set({ isSyncing: false, isSaving: false });
          }
        },

        /**
         * Get pending event types for a contact (for UI display)
         * Returns null if no pending edit exists
         */
        getContactPendingEventTypes: (contactId: number) => {
          const edit = get().contactEdits.get(contactId);
          return edit ? edit.newEventTypes : null;
        },

        // ======================================
        // Filters & Preferences Actions
        // ======================================

        /**
         * Update filters
         */
        updateFilters: (newFilters: Partial<EPGFilters>) => {
          set((state) => ({
            filters: {
              ...state.filters,
              ...newFilters,
            },
          }));
        },

        /**
         * Update view preferences
         */
        updateViewPreferences: (newPreferences: Partial<EPGViewPreferences>) => {
          set((state) => ({
            viewPreferences: {
              ...state.viewPreferences,
              ...newPreferences,
            },
          }));
        },

        /**
         * Reset filters to default
         */
        resetFilters: () => {
          set({ filters: defaultFilters });
          get().loadOrganizations(1); // Reload with default filters
        },

        /**
         * Set the currently selected month for editing
         */
        setSelectedMonth: (month: MonthCode) => {
          set({ selectedMonth: month });
          logger.log(`[EPG Store] Selected month: ${month}`);
        },

        /**
         * Set the currently selected year for calendar view
         */
        setSelectedYear: (year: number) => {
          set({ selectedOrgYear: year });
          logger.log(`[EPG Store] Selected year: ${year}`);
        },

        /**
         * Set which categories are visible in the grid
         */
        setVisibleCategories: (categories: Set<string>) => {
          set({ visibleCategories: categories });
          logger.log(`[EPG Store] Visible categories: ${Array.from(categories).join(', ')}`);
        },

        /**
         * Toggle a single category's visibility
         */
        toggleCategoryVisibility: (category: string) => {
          set((state) => {
            const newVisibleCategories = new Set(state.visibleCategories);
            if (newVisibleCategories.has(category)) {
              newVisibleCategories.delete(category);
            } else {
              newVisibleCategories.add(category);
            }
            logger.log(`[EPG Store] Toggled ${category}, visible categories: ${Array.from(newVisibleCategories).join(', ')}`);
            return { visibleCategories: newVisibleCategories };
          });
        },

        /**
         * Set sort field and direction (P4-T6)
         * @param field - Field to sort by (e.g., 'name', 'address', 'owner')
         * @param direction - Sort direction ('asc' or 'desc'), defaults to toggling current
         */
        setSortField: (field: string | null, direction?: 'asc' | 'desc') => {
          const state = get();

          // If same field clicked, toggle direction
          let newDirection: 'asc' | 'desc' = direction || 'asc';
          if (!direction && state.sortField === field) {
            newDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
          }

          set({
            sortField: field,
            sortDirection: newDirection,
          });

          logger.log(`[EPG Store] Sort changed: ${field} ${newDirection}`);
        },

        // ======================================
        // Palette Drag & Drop Actions (Single-Month Mode)
        // ======================================

        /**
         * Start dragging an event from the palette
         * Sets global drag state for visual feedback across grid
         */
        startPaletteDrag: (category: string, eventId: number, eventLabel: string, color: string) => {
          set({
            isDraggingFromPalette: true,
            draggedCategory: category,
            draggedEventId: eventId,
            draggedEventLabel: eventLabel,
            draggedEventColor: color,
          });
          logger.log(`[EPG Store] Palette drag started: ${eventLabel} (${category})`);
        },

        /**
         * End dragging an event from the palette
         * Clears all drag state
         */
        endPaletteDrag: () => {
          set({
            isDraggingFromPalette: false,
            draggedCategory: null,
            draggedEventId: null,
            draggedEventLabel: null,
            draggedEventColor: null,
          });
          logger.log(`[EPG Store] Palette drag ended`);
        },

        /**
         * Start multi-add mode (Shift+Drop stamp mode)
         * Allows clicking on multiple cells to add the same event
         */
        startMultiAddMode: (category: string, eventId: number, eventLabel: string, color: string) => {
          set({
            isMultiAddMode: true,
            multiAddCategory: category,
            multiAddEventId: eventId,
            multiAddEventLabel: eventLabel,
            multiAddEventColor: color,
          });
          logger.log(`[EPG Store] Multi-add mode started: ${eventLabel} (${category})`);
        },

        /**
         * End multi-add mode (release Shift key)
         * Clears all multi-add state
         */
        endMultiAddMode: () => {
          set({
            isMultiAddMode: false,
            multiAddCategory: null,
            multiAddEventId: null,
            multiAddEventLabel: null,
            multiAddEventColor: null,
          });
          logger.log(`[EPG Store] Multi-add mode ended`);
        },

        // ======================================
        // Error Handling Actions
        // ======================================

        /**
         * Clear error message
         */
        clearError: () => {
          set({ error: null });
        },

        // ======================================
        // UI State Actions
        // ======================================

        /**
         * Set pending changes modal open state
         */
        setPendingChangesModalOpen: (isOpen: boolean) => {
          set({ isPendingChangesModalOpen: isOpen });
        },

        // ======================================
        // Keyboard Navigation Actions (P2-T09)
        // ======================================

        /**
         * Set the focused cell for keyboard navigation
         */
        setFocusedCell: (cellKey: string | null) => {
          set({ focusedCell: cellKey });
          if (cellKey) {
            logger.log(`[EPG Store] Focused cell: ${cellKey}`);
          }
        },

        // ======================================
        // Saved Filter Presets Actions (Phase 4)
        // ======================================

        /**
         * Save current filters as a preset
         */
        saveFilterPreset: (name: string) => {
          const { filters, savedFilters } = get();

          const preset: SavedFilterPreset = {
            name,
            filters: { ...filters },
            createdAt: savedFilters[name]?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          set({
            savedFilters: {
              ...savedFilters,
              [name]: preset,
            },
          });

          logger.log(`[EPG Store] Saved filter preset: ${name}`);
        },

        /**
         * Load a saved filter preset
         */
        loadFilterPreset: (name: string) => {
          const { savedFilters } = get();

          const preset = savedFilters[name];
          if (!preset) {
            logger.warn(`[EPG Store] Filter preset not found: ${name}`);
            return;
          }

          set({
            filters: {
              ...preset.filters,
              savedFilterName: name,
            },
          });

          logger.log(`[EPG Store] Loaded filter preset: ${name}`);

          // Reload organizations with new filters
          get().loadOrganizations(1);
        },

        /**
         * Delete a saved filter preset
         */
        deleteFilterPreset: (name: string) => {
          const { savedFilters } = get();

          const { [name]: _, ...remaining } = savedFilters;

          set({ savedFilters: remaining });

          logger.log(`[EPG Store] Deleted filter preset: ${name}`);
        },

        /**
         * Get list of saved filter names
         */
        getSavedFilterNames: () => {
          return Object.keys(get().savedFilters);
        },

        // ======================================
        // Undo/Redo Actions
        // ======================================

        /**
         * Undo last change (max 50 items in history)
         */
        undo: () => {
          const { undoStack, eventsByMonth, redoStack, selectedOrgId } = get();

          if (undoStack.length === 0 || !eventsByMonth || !selectedOrgId) {
            logger.warn('[EPG Store] Nothing to undo');
            return;
          }

          // Pop last state from undo stack
          const previousState = undoStack[undoStack.length - 1];
          const newUndoStack = undoStack.slice(0, -1);

          // Push current state to redo stack (limit to 50 items)
          const newRedoStack = [...redoStack, eventsByMonth].slice(-50);

          // Update unsaved changes to reflect new state
          const updates: BatchUpdateItem[] = Object.entries(previousState).map(([monthCode, data]) => ({
            org_id: selectedOrgId,
            month: monthCode as MonthCode,
            event_ids: data.selected_ids,
          }));

          set({
            eventsByMonth: previousState,
            unsavedChanges: {
              org_id: selectedOrgId,
              org_name: get().selectedOrgName || `Organization ${selectedOrgId}`,
              updates,
              timestamp: new Date().toISOString(),
            },
            undoStack: newUndoStack,
            redoStack: newRedoStack,
          });

          logger.log('[EPG Store] Undo successful');
        },

        /**
         * Redo last undone change (max 50 items in history)
         */
        redo: () => {
          const { redoStack, eventsByMonth, undoStack, selectedOrgId } = get();

          if (redoStack.length === 0 || !eventsByMonth || !selectedOrgId) {
            logger.warn('[EPG Store] Nothing to redo');
            return;
          }

          // Pop last state from redo stack
          const nextState = redoStack[redoStack.length - 1];
          const newRedoStack = redoStack.slice(0, -1);

          // Push current state to undo stack (limit to 50 items)
          const newUndoStack = [...undoStack, eventsByMonth].slice(-50);

          // Update unsaved changes to reflect new state
          const updates: BatchUpdateItem[] = Object.entries(nextState).map(([monthCode, data]) => ({
            org_id: selectedOrgId,
            month: monthCode as MonthCode,
            event_ids: data.selected_ids,
          }));

          set({
            eventsByMonth: nextState,
            unsavedChanges: {
              org_id: selectedOrgId,
              org_name: get().selectedOrgName || `Organization ${selectedOrgId}`,
              updates,
              timestamp: new Date().toISOString(),
            },
            undoStack: newUndoStack,
            redoStack: newRedoStack,
          });

          logger.log('[EPG Store] Redo successful');
        },

        // ======================================
        // Single Company View Actions
        // ======================================

        /**
         * Set view mode (spreadsheet or single-company)
         */
        setViewMode: (mode: EPGViewMode) => {
          set({ viewMode: mode });
        },

        /**
         * Set current company index directly
         */
        setCurrentCompanyIndex: (index: number) => {
          const sortedOrgs = get().getSortedOrganizations();
          const maxIndex = Math.max(0, sortedOrgs.length - 1);
          const validIndex = Math.max(0, Math.min(index, maxIndex));
          set({ currentCompanyIndex: validIndex });
        },

        /**
         * Navigate to next company in sorted list
         * Also updates selectedOrgId to sync with Contacts Panel
         */
        navigateToNextCompany: () => {
          const { currentCompanyIndex } = get();
          const sortedOrgs = get().getSortedOrganizations();
          const maxIndex = sortedOrgs.length - 1;

          if (currentCompanyIndex < maxIndex) {
            const newIndex = currentCompanyIndex + 1;
            const nextOrg = sortedOrgs[newIndex];
            set({
              currentCompanyIndex: newIndex,
              selectedOrgId: nextOrg?.id ?? null,
              selectedOrgName: nextOrg?.name ?? null,
              lastSelectedOrgId: nextOrg?.id ?? null,
            });
          }
        },

        /**
         * Navigate to previous company in sorted list
         * Also updates selectedOrgId to sync with Contacts Panel
         */
        navigateToPreviousCompany: () => {
          const { currentCompanyIndex } = get();
          const sortedOrgs = get().getSortedOrganizations();

          if (currentCompanyIndex > 0) {
            const newIndex = currentCompanyIndex - 1;
            const prevOrg = sortedOrgs[newIndex];
            set({
              currentCompanyIndex: newIndex,
              selectedOrgId: prevOrg?.id ?? null,
              selectedOrgName: prevOrg?.name ?? null,
              lastSelectedOrgId: prevOrg?.id ?? null,
            });
          }
        },

        /**
         * Navigate to a specific company by ID
         * Also updates selectedOrgId to sync with Contacts Panel
         */
        navigateToCompany: (orgId: number) => {
          const sortedOrgs = get().getSortedOrganizations();
          const index = sortedOrgs.findIndex(org => org.id === orgId);

          if (index !== -1) {
            const org = sortedOrgs[index];
            set({
              currentCompanyIndex: index,
              selectedOrgId: org.id,
              selectedOrgName: org.name,
              lastSelectedOrgId: orgId,  // Save last selected company
            });

            // Save to backend user settings as well (TODO: implement API call)
            // This would save via apiClient to PostgreSQL app_settings table
            logger.log(`[EPG Store] Navigated to company ${orgId} (${org.name}), saved as last selected`);
          }
        },

        /**
         * Get organizations sorted by the selected field and direction (P4-T6)
         * Supports sorting by: name, address, city, state, owner, etc.
         */
        getSortedOrganizations: () => {
          const { organizations, sortField, sortDirection } = get();

          // Create a copy to avoid mutating original array
          const sorted = [...organizations];

          // If no sort field specified, default to name
          const field = sortField || 'name';

          sorted.sort((a, b) => {
            let aValue: string | number = '';
            let bValue: string | number = '';

            // Get values based on field
            switch (field) {
              case 'name':
                aValue = a.name || '';
                bValue = b.name || '';
                break;
              case 'address':
                aValue = a.address || '';
                bValue = b.address || '';
                break;
              case 'city':
                aValue = a.address_locality || '';
                bValue = b.address_locality || '';
                break;
              case 'state':
                aValue = a.address_admin_area_level_1 || '';
                bValue = b.address_admin_area_level_1 || '';
                break;
              case 'owner':
                aValue = a.owner_name || '';
                bValue = b.owner_name || '';
                break;
              case 'last_activity':
                // For dates, use timestamp comparison
                aValue = a.last_activity_date ? new Date(a.last_activity_date).getTime() : 0;
                bValue = b.last_activity_date ? new Date(b.last_activity_date).getTime() : 0;
                break;
              default:
                // For any other field, try to access it directly from Pipedrive field hash
                // EPGOrganization has [fieldHash: string]: unknown index signature
                const aFieldValue = a[field];
                const bFieldValue = b[field];
                aValue = typeof aFieldValue === 'string' || typeof aFieldValue === 'number' ? aFieldValue : '';
                bValue = typeof bFieldValue === 'string' || typeof bFieldValue === 'number' ? bFieldValue : '';
            }

            // Handle numeric vs string comparison
            const aNum = Number(aValue);
            const bNum = Number(bValue);
            const bothNumbers = !isNaN(aNum) && !isNaN(bNum);

            let comparison = 0;
            if (bothNumbers) {
              comparison = aNum - bNum;
            } else {
              // String comparison (case-insensitive)
              comparison = String(aValue).localeCompare(String(bValue), undefined, { sensitivity: 'base' });
            }

            // Apply sort direction
            return sortDirection === 'desc' ? -comparison : comparison;
          });

          return sorted;
        },

        /**
         * Get the current organization based on index
         */
        getCurrentOrganization: () => {
          const { currentCompanyIndex } = get();
          const sortedOrgs = get().getSortedOrganizations();
          return sortedOrgs[currentCompanyIndex] || null;
        },

        // ======================================
        // Cell Edit Panel Actions (Sidebar Edit Mode)
        // ======================================

        /**
         * Start editing a cell - opens the edit panel in the sidebar
         * Replaces the Event Palette while editing
         */
        startCellEdit: (orgId: number, category: string, month: MonthCode, orgName: string) => {
          logger.log(`[EPG Store] Starting cell edit: ${orgName} - ${category} - ${month}`);
          set({
            editingCell: { orgId, category, month, orgName },
          });
        },

        /**
         * End cell editing - closes the edit panel and restores Event Palette
         */
        endCellEdit: () => {
          const { editingCell } = get();
          if (editingCell) {
            logger.log(`[EPG Store] Ending cell edit: ${editingCell.orgName} - ${editingCell.category} - ${editingCell.month}`);
          }
          set({ editingCell: null });
        },
      }),
      {
        name: 'epg-storage', // localStorage key
        partialize: (state) => ({
          // Only persist view preferences, unsaved changes, visible categories, saved filters, viewMode, and lastSelectedOrgId
          viewPreferences: state.viewPreferences,
          unsavedChanges: state.unsavedChanges,
          visibleCategories: Array.from(state.visibleCategories), // Convert Set to Array for persistence
          selectedContactIds: Array.from(state.selectedContactIds), // Convert Set to Array for persistence
          savedFilters: state.savedFilters,
          viewMode: state.viewMode, // Persist view mode preference
          lastSelectedOrgId: state.lastSelectedOrgId, // Persist last selected company
          currentCompanyIndex: state.currentCompanyIndex, // Persist current index
          _migrationVersion: 1, // Track migration version for schema migrations
        }),
        // Custom merge function to handle Set deserialization and migrations
        merge: (persistedState: Partial<EPGState> & { _migrationVersion?: number }, currentState: EPGState) => {
          // Build merged viewPreferences with defaults first, then persisted values
          const mergedViewPreferences = {
            ...DEFAULT_EPG_PREFERENCES,
            ...persistedState?.viewPreferences,
          };

          // MIGRATION v1 (2025-12-19): Reset highlightEmptyMonths to false for users
          // who had it set to true from the old default. This is a one-time migration.
          // We use a migration flag to track if this migration has been applied.
          const migrationVersion = persistedState?._migrationVersion || 0;
          if (migrationVersion < 1) {
            // Force highlightEmptyMonths to false (new default)
            mergedViewPreferences.highlightEmptyMonths = false;
            // Ensure zoom has a proper default if not set
            if (!mergedViewPreferences.zoom) {
              mergedViewPreferences.zoom = 100;
            }
          }

          return {
            ...currentState,
            ...persistedState,
            _migrationVersion: 1, // Track current migration version
            viewPreferences: mergedViewPreferences,
            visibleCategories: persistedState?.visibleCategories
              ? new Set(persistedState.visibleCategories)
              : currentState.visibleCategories,
            selectedContactIds: persistedState?.selectedContactIds
              ? new Set(persistedState.selectedContactIds)
              : currentState.selectedContactIds,
            savedFilters: persistedState?.savedFilters || {},
          };
        },
      }
    ),
    { name: 'EPG Store' }
  )
);

/**
 * Computed selectors (use outside component)
 */

/**
 * Check if there are unsaved changes
 */
export const selectHasUnsavedChanges = (state: EPGState): boolean => {
  return state.unsavedChanges !== null;
};

/**
 * Get filtered organizations (client-side filtering for quick filters and search)
 */
export const selectFilteredOrganizations = (state: EPGState): EPGOrganization[] => {
  let filtered = state.organizations;

  // Search filter: Filter by company name (case-insensitive)
  if (state.filters.search && state.filters.search.trim() !== '') {
    const searchTerm = state.filters.search.toLowerCase().trim();
    filtered = filtered.filter((org) =>
      org.name.toLowerCase().includes(searchTerm)
    );
  }

  // Quick filter: Recent activity (last 30 days)
  if (state.filters.quickFilters.recentActivity) {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    filtered = filtered.filter((org) => {
      if (!org.last_activity_date) return false;
      const activityDate = new Date(org.last_activity_date);
      return activityDate >= thirtyDaysAgo;
    });
  }

  // Note: hasEvents filter removed - not supported by backend API
  // Backend only returns organizations with is_15dp_opportunity = true

  return filtered;
};

/**
 * Check if undo is available
 */
export const selectCanUndo = (state: EPGState): boolean => {
  return state.undoStack.length > 0;
};

/**
 * Check if redo is available
 */
export const selectCanRedo = (state: EPGState): boolean => {
  return state.redoStack.length > 0;
};

/**
 * Check if a specific cell has pending changes
 */
export const selectCellHasPendingChanges = (
  state: EPGState,
  orgId: number,
  category: string,
  month: MonthCode
): boolean => {
  const cellKey = `${orgId}-${category}-${month}`;
  return state.cellEdits.has(cellKey);
};

/**
 * Get cell edit details
 */
export const selectCellEdit = (
  state: EPGState,
  orgId: number,
  category: string,
  month: MonthCode
) => {
  const cellKey = `${orgId}-${category}-${month}`;
  return state.cellEdits.get(cellKey);
};

// ==================== Cloud Sync ====================

// Cloud sync for EPG view preferences (NOT organization data)
const epgViewCloudSync = createCloudSync<{
  selectedMonth: MonthCode;
  selectedOrgYear: number;
  viewPreferences: EPGViewPreferences;
  visibleCategories: string[];
  lastSelectedOrgId: number | null;
}>({
  key: 'epg-view-preferences',
  namespace: 'epg',
  debounceMs: 2000, // Longer debounce for view changes
});

// Sync view preferences only (not organization data)
// MOVED TO useEPGStoreCloudSync() hook to prevent module-level subscription
// (see useEPGStoreCloudSync() below)

/**
 * Load from cloud on app initialization
 * Call this during app startup to restore preferences from PostgreSQL
 */
export async function initEPGViewFromCloud(): Promise<void> {
  try {
    const cloudState = await epgViewCloudSync.loadFromCloud();
    if (cloudState) {
      // Convert visibleCategories array back to Set
      const stateToApply = {
        ...cloudState,
        visibleCategories: new Set(cloudState.visibleCategories),
      };
      useEPGStore.setState(stateToApply);
      logger.log('[EPG Store] Loaded view preferences from cloud');
    } else {
      // No cloud state found - use defaults
      logger.log('[EPG Store] No cloud preferences found, using defaults');
    }
  } catch (error) {
    // If loading from cloud fails, continue with default state
    logger.error('[EPG Store] Failed to load view preferences from cloud, using defaults:', error);
    // Defaults are already set in the initial state
  }
}

/**
 * Hook to sync EPG view preferences to cloud
 *
 * Sets up a Zustand subscription within React lifecycle.
 * This replaces the module-level subscription to prevent
 * uncontrolled subscriptions at import time.
 *
 * Call this hook once in your app root component to enable
 * automatic cloud syncing of view preferences.
 */
export function useEPGStoreCloudSync() {
  useEffect(() => {
    const unsubscribe = useEPGStore.subscribe((state) => {
      epgViewCloudSync.syncToCloud({
        selectedMonth: state.selectedMonth,
        selectedOrgYear: state.selectedOrgYear,
        viewPreferences: state.viewPreferences,
        visibleCategories: Array.from(state.visibleCategories), // Convert Set to Array
        lastSelectedOrgId: state.lastSelectedOrgId,
      });
    });

    return () => unsubscribe();
  }, []);
}

/**
 * Export default
 */
export default useEPGStore;
